import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from './LoadingSpinner';

/**
 * Route guard.
 *
 *   <Route element={<ProtectedRoute />}>                        signed in
 *   <Route element={<ProtectedRoute requires="page.admin" />}>  holds a code
 *   <Route element={<ProtectedRoute requiresAny={['a','b']} />}>holds either
 *   <Route element={<ProtectedRoute adminOnly />}>              legacy, still works
 *
 * On denial the user is bounced to their own landing path rather than a
 * hardcoded /dashboard -- a committee member has no dashboard, so sending
 * them there would have produced a redirect loop.
 */
const ProtectedRoute = ({ requires, requiresAny, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading, can, canAny, landingPath } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    // Remember where they were headed so login can send them back.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to={landingPath} replace />;
  }

  if (requires) {
    const codes = Array.isArray(requires) ? requires : [requires];
    if (!can(codes)) {
      return <Navigate to={landingPath} replace />;
    }
  }

  if (requiresAny && requiresAny.length > 0) {
    if (!canAny(requiresAny)) {
      return <Navigate to={landingPath} replace />;
    }
  }

  return <Outlet />;
};

export default ProtectedRoute;
